import { t } from 'i18next';
import {
  css,
  html,
  LitElement,
  nothing,
  PropertyValues,
  TemplateResult,
} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import {
  PrintOptionCollapsibleDto,
  PrintOptionCollapsibleFilterDto,
  PrintOptionCollapsibleFilterOptionDto,
} from '@/api-sdk';
import { FocusManagementMixin } from '@/components/configurator/focus-management-step-mixin';
import { BREAKPOINT_MEDIUM_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';

import '@/components/configurator/step-collapsible/fg-collapsible-filter-item';
import { SubStepButtonType } from '@/types';

@customElement('fg-collapsible-print-option')
export class FgCollapsiblePrintOption extends FocusManagementMixin(LitElement) {
  @property({ type: Number })
  index!: number;

  @property({ type: Number })
  totalOptions = 0;

  @property({ type: Array })
  filters!: PrintOptionCollapsibleFilterDto[];

  @property({ type: Boolean })
  open?: boolean;

  @property({ type: Boolean })
  valid?: boolean;

  @property({ type: String })
  unprintedQueryString: string | null | undefined;

  @property({ type: Object })
  printOption?: PrintOptionCollapsibleDto;

  private _nextSubstepButtons: Map<number, SubStepButtonType> = new Map();

  constructor() {
    super();
    this.setupFocusManagement('printOption');
  }

  updateComponent(queryString: string | null | undefined): void {
    if (!queryString) {
      console.error('Missing query string');
      return;
    }

    const lastSubStepIndex = this.filters.length - 1;
    const lastFilter = this.filters[lastSubStepIndex];

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
            subStepIndex: lastSubStepIndex,
            filterOptionLayout: lastFilter?.filterOptionLayout,
          },
        },
      }),
    );
  }

  isOptionFocussed(subStepIndex: number, filterOptionIndex: number): boolean {
    if (filterOptionIndex !== 0) return false;

    return subStepIndex === 0
      ? this.focusFirstOption
      : (this._nextSubstepButtons.get(subStepIndex)?.focusFirstOption ?? false);
  }

  protected willUpdate(_changedProperties: PropertyValues) {
    super.willUpdate(_changedProperties);

    const focusIndex = this.getFocusIndex();
    if (
      focusIndex === -1 ||
      focusIndex === 0 ||
      !_changedProperties.has('filters')
    ) {
      return;
    }

    const nextSubStepButton = this.getOrCreateNextSubstepButton(focusIndex);
    nextSubStepButton.showGoToNextStepButton = true;
    this._nextSubstepButtons.set(focusIndex, nextSubStepButton);

    if (nextSubStepButton.showGoToNextStepButton) {
      this.focusSubstepButtonWhenReady(nextSubStepButton, focusIndex);
    }
  }

  private getFocusIndex(): number {
    return this.filters?.findIndex(filter => filter.focus) ?? -1;
  }

  private getOrCreateNextSubstepButton(index: number): SubStepButtonType {
    const existingButton = this._nextSubstepButtons.get(index);
    if (existingButton) return existingButton;

    const newButton: SubStepButtonType = {
      index,
      ref: createRef(),
      focusFirstOption: false,
      showGoToNextStepButton: false,
    };
    this._nextSubstepButtons.set(index, newButton);
    return newButton;
  }

  private focusSubstepButtonWhenReady(
    nextSubStepButton: SubStepButtonType,
    focusIndex: number,
  ): void {
    this.updateComplete.then(() => {
      const element = nextSubStepButton.ref.value as HTMLElement | undefined;
      if (!element) return;

      element.focus();
      element.addEventListener(
        'blur',
        () => {
          nextSubStepButton.showGoToNextStepButton = false;
          this._nextSubstepButtons.set(focusIndex, nextSubStepButton);
        },
        { once: true },
      );
    });
  }

  focusFirstOptionSubstepElement(subStepIndex: number): void {
    if (subStepIndex === -1) return;

    const nextSubStepButton = this.getOrCreateNextSubstepButton(subStepIndex);

    const updateFocusState = (value: boolean) => {
      nextSubStepButton.focusFirstOption = value;
      this._nextSubstepButtons.set(subStepIndex, nextSubStepButton);
      this.requestUpdate();
    };

    updateFocusState(true);
    this.updateComplete.then(() => updateFocusState(false));
  }

  renderNextSubstepButton(
    stepName = '',
    subStepIndex?: number,
  ): TemplateResult | typeof nothing {
    const targetIndex = subStepIndex ?? this.getFocusIndex();
    const nextSubStepButton = this._nextSubstepButtons.get(targetIndex);

    if (!nextSubStepButton?.showGoToNextStepButton) return nothing;

    return html`
      <button
        ${ref(nextSubStepButton.ref as Ref<HTMLButtonElement>)}
        class="sr-only"
        @click=${() => this.focusFirstOptionSubstepElement(targetIndex)}
      >
        ${t('actions.proceedFirstOption', { stepName })}
      </button>
    `;
  }

  render() {
    if (!Array.isArray(this.filters) || this.filters.length === 0)
      return nothing;

    return html`
      ${this.renderNextStepButton(this.printOption?.title)}
      <div ?hidden=${!this.open}>
        ${this.filters.map(
          (filter: PrintOptionCollapsibleFilterDto, subStepIndex: number) => {
            const filterSubStepId = `filter-substep-${subStepIndex}`;
            return html`
              <div
                class="wrapper
                ${classMap({
                  selected: Boolean(filter.focus),
                })}"
              >
                <h3 class="filter-question" id=${filterSubStepId}>
                  ${filter.filterQuestion}
                </h3>
                <ul
                  aria-labelledby=${filterSubStepId}
                  class="filter-options
                ${classMap({
                    'has-most-chosen': filter.filterOptions?.some(
                      opt => opt.mostChosen,
                    ),
                    [`${filter.filterOptionLayout}`]:
                      filter.filterOptionLayout != null,
                  })}"
                >
                  ${filter.filterOptions?.map(
                    (
                      option: PrintOptionCollapsibleFilterOptionDto,
                      filterOptionIndex: number,
                    ) => html`
                      <fg-collapsible-filter-item
                        .option=${option}
                        .filterOptionLayout=${filter.filterOptionLayout}
                        .index=${this.index}
                        .subStepIndex=${subStepIndex}
                        .filterOptionIndex=${filterOptionIndex}
                        .filterIndex=${this.filters.length}
                        .focussed=${this.isOptionFocussed(
                          subStepIndex,
                          filterOptionIndex,
                        )}
                      ></fg-collapsible-filter-item>
                    `,
                  )}
                </ul>
                ${this.renderNextSubstepButton(
                  filter.filterQuestion,
                  subStepIndex,
                )}
              </div>
            `;
          },
        )}
        ${this.unprintedQueryString !== null &&
        this.unprintedQueryString !== undefined &&
        this.index === 0
          ? html`
              <div class="unprinted-button-container">
                <button
                  @click=${() =>
                    this.updateComponent(this.unprintedQueryString)}
                >
                  ${t('actions.iDontWantAnyPrinting')}
                </button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .wrapper {
        border: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        margin-top: 16px;
        padding: 16px;
      }

      .wrapper.selected {
        background-color: var(
          --fg-tertiary--light,
          var(--fg-fallback-tertiary--light)
        );
        border: 1px solid var(--fg-info, var(--fg-fallback-info));
      }

      .filter-options.image,
      .filter-options.default {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(1, minmax(0, 1fr));
      }

      .filter-options {
        clear: left;
        flex-wrap: wrap;
        display: flex;
        gap: 8px;
      }

      .filter-options.has-most-chosen {
        padding-top: 12px;
      }

      .filter-question {
        clear: both;
        float: left;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 8px;
      }

      .unprinted-button-container {
        margin: 16px 0 0 16px;
      }

      .unprinted-button-container button {
        color: var(--fg-primary, var(--fg-fallback-primary));
        font-size: 1rem;
        text-decoration: underline;
      }

      .unprinted-button-container button:hover {
        text-decoration: none;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .filter-options.image {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .filter-options.default {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .wrapper {
          padding: 16px 8px;
        }

        .unprinted-button-container {
          margin: 8px 0 0 0;
        }
      }
    `,
  ];
}
